import { categoryLabel } from "../config/productCategories";
import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useId, Fragment } from "react";
import { createPortal } from "react-dom";
import type { CategoryV2 } from "../services/CategoriesService";
import { useTranslation } from "../context/LanguageContext";
import { useRetailer } from "../hooks/useRetailer";
import {
  getSelectableProductCategories,
  getStoreAddress,
  filterCategoriesBySearch,
  isProductCategorySelected,
  NO_CATEGORIES,
  toggleProductCategorySelection,
} from "./ProductFiltersModal.utils";
import { useNavigate } from "react-router-dom";
import { X, RotateCcw, HelpCircle, Shield, ChevronLeft, ChevronDown, Globe, Search, Store, Circle, CircleCheck, CirclePlus, Clock3, EyeOff } from "lucide-react";
import { toast } from "react-toastify";
import { useStores, type TargetStore, type CostcoStore } from "../hooks/useStores";
import { useTour } from "../context/TourContext";
import { useUserContext } from "../context/UserContext";
import AdminStorePicker from "./AdminStorePicker";
import { isOnlineOnlyRetailer, supportsCrazyDeals } from "../config/retailers";
import { getWeeklyStoreChangeLock, isFreeTier } from "../utils/userUtils";
import { useStoreSlotPlan } from "../hooks/useStoreSlotPlan";
import SheetStoreEditor from "./SheetStoreEditor";
import { formatHomeDepotStoreId } from "./filtertypes/StoresFilter.utils";
import { buildStatusOptions, type StatusCounts, type StatusValue } from "./ProductsHeader.utils";
import type {
  WalmartStore,
  HomeDepotStore,
  DollarGeneralStore,
} from "../utils/storeUtils";

interface StoreFilterOption {
  id: string;
  name: string;
  isAll?: boolean;
  isExtra?: boolean;
  distanceMiles?: number;
}

interface ProductFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: TabId;
  selectedStoreId: string;
  onStoreChange: (storeId: string) => void;
  storeFilterOptions: StoreFilterOption[];
  /** Extra stores the member hid, shown under a collapsed "Hidden stores (n)" row. */
  hiddenExtraStoreOptions?: StoreFilterOption[];
  /** Present when extra stores can be hidden (signed-in member). */
  onHideExtraStore?: (storeId: string) => void;
  onShowExtraStore?: (storeId: string) => void;
  hasOnlineOption?: boolean;
  selectedCategories: string[]; // Empty array = all categories selected
  onCategoriesChange: (categoryIds: string[]) => void;
  categories: (CategoryV2 & { count?: number })[];
  categoriesLoading: boolean;
  showNewestOnly: boolean;
  onShowNewestOnlyChange: (showNewest: boolean) => void;
  showVariantsOnly?: boolean;
  onShowVariantsOnlyChange?: (show: boolean) => void;
  hideCashback?: boolean;
  onHideCashbackChange?: (hide: boolean) => void;
  showHotDeals: boolean;
  setShowHotDeals: (show: boolean) => void;
  showPennyItems: boolean;
  setShowPennyItems: () => void;
  shouldScrollToCategories?: boolean;
  minStorePrice?: number;
  maxStorePrice?: number;
  minMsrp?: number;
  maxMsrp?: number;
  minDiscount?: number;
  onMinStorePriceChange?: (price: number | undefined) => void;
  onMaxStorePriceChange?: (price: number | undefined) => void;
  onMinMsrpChange?: (price: number | undefined) => void;
  onMaxMsrpChange?: (price: number | undefined) => void;
  onMinDiscountChange?: (discount: number | undefined) => void;
  showInStockOnly?: boolean;
  onShowInStockOnlyChange?: (showInStockOnly: boolean) => void;
  productStatusFilter?: StatusValue;
  onProductStatusFilterChange?: (filter: StatusValue) => void;
  productStatusCounts?: StatusCounts;
  showSaved?: boolean;
  hiddenProductsCount?: number;
  onManageHiddenProducts?: () => void;
  onResetFilters?: () => void;
}

type TabId = "stores" | "categories" | "price" | "other";

function ToggleSwitch({
  checked,
  onChange,
  label,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      className={`relative w-10 h-6 rounded-full flex-shrink-0 ml-3 transition-colors duration-200 ${
        checked ? "bg-primary-600" : "bg-gray-200 dark:bg-dark-600"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

const FILTER_INPUT_CLASS =
  "block w-full py-2.5 bg-gray-100/80 dark:bg-dark-800 border border-transparent rounded-xl text-sm font-medium tabular-nums text-gray-900 dark:text-white placeholder:font-normal placeholder-gray-400 dark:placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/15 focus:border-primary-500/30 focus:bg-white dark:focus:bg-dark-700 transition-all";

const DISCOUNT_PRESETS = [25, 50, 70, 90];

const ProductFiltersModal: React.FC<ProductFiltersModalProps> = ({
  isOpen,
  onClose,
  initialTab,
  selectedStoreId,
  onStoreChange,
  storeFilterOptions,
  hiddenExtraStoreOptions = [],
  onHideExtraStore,
  onShowExtraStore,
  hasOnlineOption = false,
  selectedCategories,
  onCategoriesChange,
  categories,
  categoriesLoading,
  showNewestOnly,
  onShowNewestOnlyChange,
  showVariantsOnly = false,
  onShowVariantsOnlyChange,
  hideCashback = true,
  onHideCashbackChange,
  showHotDeals,
  setShowHotDeals,
  showPennyItems,
  setShowPennyItems,
  shouldScrollToCategories = false,
  minStorePrice,
  maxStorePrice,
  minMsrp,
  maxMsrp,
  minDiscount,
  onMinStorePriceChange,
  onMaxStorePriceChange,
  onMinMsrpChange,
  onMaxMsrpChange,
  onMinDiscountChange,
  showInStockOnly = true,
  onShowInStockOnlyChange,
  productStatusFilter = "all",
  onProductStatusFilterChange,
  productStatusCounts,
  showSaved = true,
  hiddenProductsCount = 0,
  onManageHiddenProducts,
  onResetFilters,
}) => {
  const { t, currentLanguage } = useTranslation();
  const modalId = useId();
  const { startTour } = useTour();
  const { user } = useUserContext();
  const navigate = useNavigate();
  const retailer = useRetailer();
  const isAdmin = user?.admin === true;
  const isOnlineRetailer = isOnlineOnlyRetailer(retailer);
  const shouldHideStoresTab = isOnlineRetailer || isFreeTier(user);
  const [activeTab, setActiveTab] = useState<TabId>(shouldHideStoresTab ? "categories" : "stores");
  const [categorySearch, setCategorySearch] = useState("");
  const [showAdminStoreLookup, setShowAdminStoreLookup] = useState(false);
  const [showHiddenExtraStores, setShowHiddenExtraStores] = useState(false);
  // Editing or adding a store shows the store picker for that slot, in this same sheet.
  const [pickSlot, setPickSlot] = useState<number | null>(null);
  const isPickingStore = pickSlot != null && activeTab === "stores" && !shouldHideStoresTab;
  const showSubview = showAdminStoreLookup || isPickingStore;
  const storeSlots = useStoreSlotPlan();
  const savedStoreIds = storeSlots.selectedStores.map((id) => formatHomeDepotStoreId(id));
  const openSlotCount = Math.max(0, storeSlots.storeLimit - savedStoreIds.length);
  // Basic plans can change stores once a day; say when the next change opens up.
  const storeLock = getWeeklyStoreChangeLock(user, storeSlots.retailer);
  const storeLockedUntil = storeLock?.isLocked
    ? storeLock.nextAllowedDate.toLocaleString(currentLanguage === "es" ? "es-US" : "en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })
    : null;
  const openStoreSlot = (slotIndex: number) => {
    if (storeLockedUntil) {
      toast.warn(t("storeEdit.locked", { date: storeLockedUntil }), { position: "top-right", autoClose: 6000 });
      return;
    }
    setPickSlot(slotIndex);
  };
  const statusOptions = useMemo(
    () => buildStatusOptions({ retailer, isOnlineRetailer, counts: productStatusCounts, t })
      .filter((option) => showSaved || option.value !== "saved"),
    [isOnlineRetailer, productStatusCounts, retailer, showSaved, t]
  );
  // Top Deals hands us all-zero counts because status filtering does not apply there; a list of zeros is noise.
  const hasStatusCounts =
    !productStatusCounts ||
    statusOptions.some((option) => option.value !== "saved" && (option.count ?? 0) > 0);

  // Mount + visible state to drive open/close transitions
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Mount when opening; delay-unmount after closing animation completes
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      return;
    }
    setIsVisible(false);
    const id = setTimeout(() => setIsMounted(false), 300);
    return () => clearTimeout(id);
  }, [isOpen]);

  // Trigger slide-in transition AFTER the modal has mounted in the DOM.
  // useLayoutEffect runs after DOM mutations but before paint; we force a
  // reflow to lock in the initial (offscreen) state, then flip to visible
  // so the CSS transition has a "from" state to animate from.
  useLayoutEffect(() => {
    if (!isMounted) return;
    // Force reflow so the browser commits the initial translate-y-full state
    void panelRef.current?.offsetHeight;
    setIsVisible(true);
  }, [isMounted]);

  const contentRef = useRef<HTMLDivElement>(null);

  // Load stores from S3
  const { stores: loadedStores, loading: storesLoading } = useStores<
    WalmartStore[] | HomeDepotStore[] | DollarGeneralStore[] | TargetStore[] | CostcoStore[]
  >(isOnlineRetailer ? "walmart" : retailer);

  // Function to get store address by store ID
  const storeAddress = (storeId: string) =>
    getStoreAddress({
      storeId,
      retailer,
      loadedStores,
    });

  const handleAdminStoreSelect = (storeId: string) => {
    setShowAdminStoreLookup(false);
    onStoreChange(storeId);
  };

  const handleModalClose = () => {
    if (isPickingStore) {
      setPickSlot(null);
      return;
    }
    if (showAdminStoreLookup) {
      setShowAdminStoreLookup(false);
      return;
    }
    onClose();
  };

  const adminStoreLookupButton = isAdmin && selectedStoreId !== "online" ? (
    <button
      type="button"
      onClick={() => setShowAdminStoreLookup(true)}
      className="flex min-h-11 items-center justify-center gap-1.5 rounded-lg px-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-900/20"
    >
      <Shield className="h-3.5 w-3.5" />
      {t("products.adminStoreLookupOpen")}
    </button>
  ) : null;

  // Categories filtered by search term
  const filteredCategories = useMemo(() => {
    return filterCategoriesBySearch(categories, categorySearch);
  }, [categories, categorySearch]);
  const selectableCategories = useMemo(
    () => getSelectableProductCategories(categories),
    [categories]
  );
  const selectableCategoryIDs = useMemo(
    () => selectableCategories.map((category) => category.rootKey),
    [selectableCategories]
  );
  const selectableCategoryIDSet = useMemo(
    () => new Set(selectableCategoryIDs),
    [selectableCategoryIDs]
  );
  const filteredSelectableCategories = useMemo(
    () => getSelectableProductCategories(filteredCategories),
    [filteredCategories]
  );

  const selectedCategoryCount = selectedCategories.length === 0
    ? selectableCategoryIDs.length
    : selectableCategoryIDs.filter((id) => selectedCategories.includes(id)).length;
  const allCategoriesSelected = selectableCategoryIDs.length > 0 && selectedCategoryCount === selectableCategoryIDs.length;

  // Keep saved filters within the selectable categories; a full selection collapses to [] (all).
  useEffect(() => {
    if (categoriesLoading || selectableCategoryIDs.length === 0 || selectedCategories.length === 0 || selectedCategories[0] === NO_CATEGORIES) {
      return;
    }
    const sanitized = selectedCategories.filter((category) => selectableCategoryIDSet.has(category));
    const normalized = sanitized.length === selectableCategoryIDs.length ? [] : sanitized;
    if (
      normalized.length !== selectedCategories.length ||
      normalized.some((category, index) => category !== selectedCategories[index])
    ) {
      onCategoriesChange(normalized);
    }
  }, [categoriesLoading, onCategoriesChange, selectableCategoryIDs, selectableCategoryIDSet, selectedCategories]);

  // Open the requested tab directly without scrolling past other filters.
  useEffect(() => {
    if (isOpen && shouldScrollToCategories) setActiveTab("categories");
  }, [isOpen, shouldScrollToCategories]);

  useEffect(() => {
    if (!isOpen || shouldScrollToCategories || !initialTab) return;
    setActiveTab(initialTab === "stores" && shouldHideStoresTab ? "categories" : initialTab);
  }, [initialTab, isOpen, shouldHideStoresTab, shouldScrollToCategories]);

  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [activeTab, isPickingStore]);

  // Restore the initial tab and clear search when closing.
  useEffect(() => {
    if (!isOpen) {
      setActiveTab(shouldHideStoresTab ? "categories" : "stores");
      setCategorySearch("");
      setShowAdminStoreLookup(false);
      setPickSlot(null);
    }
  }, [isOpen, shouldHideStoresTab]);

  useEffect(() => {
    if (shouldHideStoresTab && activeTab === "stores") {
      setActiveTab("categories");
    }
  }, [shouldHideStoresTab, activeTab]);

  const tabs: { id: TabId; label: string }[] = [
    ...(!shouldHideStoresTab ? [{ id: "stores" as const, label: t("products.storesTab") }] : []),
    { id: "categories", label: t("products.categoriesTab") },
    { id: "price", label: t("products.priceTab") },
    { id: "other", label: t("card.moreTab") },
  ];

  useEffect(() => {
    if (!isMounted) return;
    const previousFocus = document.activeElement;
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [isMounted]);

  useEffect(() => {
    // The focused trigger is removed when entering or leaving store lookup or the store picker.
    panelRef.current?.focus({ preventScroll: true });
  }, [showAdminStoreLookup, isPickingStore]);

  if (!isMounted) return null;

  return createPortal(
    <div
      className={`fixed inset-0 transition-colors duration-300 ease-out ${isVisible ? "bg-black/40 dark:bg-black/60" : "bg-black/0"}`}
      style={{ zIndex: 100000 }}
      onClick={(event) => { event.stopPropagation(); handleModalClose(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${modalId}-title`}
        tabIndex={-1}
        className={`fixed inset-x-0 bottom-0 mx-auto flex h-[min(34rem,calc(100dvh-2rem))] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl outline-none transition-transform duration-300 ease-out dark:bg-dark-900 ${isVisible ? "translate-y-0" : "translate-y-full"}`}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === "Escape") { event.stopPropagation(); handleModalClose(); }
          if (event.key !== "Tab") return;
          const controls = event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), a[href], [tabindex="0"]');
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }}
      >
        <div aria-hidden="true" className="flex shrink-0 justify-center pt-2 pb-1">
          <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-dark-600" />
        </div>
        <div className="flex shrink-0 items-center gap-2 border-b border-gray-200/80 px-5 pb-2 dark:border-dark-700">
          {showSubview && (
            <button type="button" onClick={() => (isPickingStore ? setPickSlot(null) : setShowAdminStoreLookup(false))} aria-label={t("common.back")} className="-ml-2 flex h-11 w-9 shrink-0 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 dark:text-dark-300 dark:hover:bg-dark-800">
              <ChevronLeft size={22} />
            </button>
          )}
          <h3 id={`${modalId}-title`} className="min-w-0 flex-1 text-[22px] font-semibold leading-7 tracking-tight text-gray-900 dark:text-white">
            {showAdminStoreLookup ? t("products.adminStorePickerTitle") : isPickingStore ? t("storeEdit.choose") : "Refine deals"}
          </h3>
          {!showSubview && (
            <button type="button" onClick={() => { onClose(); setTimeout(startTour, 350); }} aria-label="Help tour" className="flex h-11 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:text-dark-300 dark:hover:bg-dark-800">
              <HelpCircle size={22} strokeWidth={1.75} />
            </button>
          )}
          <button type="button" onClick={handleModalClose} aria-label={t("common.close")} className="-mr-2 flex h-11 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:text-dark-300 dark:hover:bg-dark-800">
            <X size={22} strokeWidth={1.75} />
          </button>
        </div>

        {!showSubview && (
          <div role="tablist" aria-label={t("products.filtersTitle")} className="flex shrink-0 border-b border-gray-200/80 px-5 dark:border-dark-700">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`${modalId}-tab-${tab.id}`}
                aria-selected={activeTab === tab.id}
                aria-controls={`${modalId}-panel`}
                tabIndex={activeTab === tab.id ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(event) => {
                  const nextIndex = event.key === "ArrowRight" ? (index + 1) % tabs.length
                    : event.key === "ArrowLeft" ? (index - 1 + tabs.length) % tabs.length
                    : event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : null;
                  if (nextIndex === null) return;
                  event.preventDefault();
                  setActiveTab(tabs[nextIndex].id);
                  document.getElementById(`${modalId}-tab-${tabs[nextIndex].id}`)?.focus();
                }}
                className={`-mb-px min-h-11 min-w-0 flex-1 border-b-2 px-1 text-xs font-semibold transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-600 min-[360px]:text-[13px] sm:text-sm ${activeTab === tab.id ? "border-primary-600 text-primary-600 dark:border-purple-400 dark:text-purple-300" : "border-transparent text-gray-500 hover:text-gray-900 dark:text-dark-300 dark:hover:text-white"}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div
          ref={contentRef}
          id={`${modalId}-panel`}
          role={showSubview ? undefined : "tabpanel"}
          aria-labelledby={showSubview ? undefined : `${modalId}-tab-${activeTab}`}
          tabIndex={showSubview ? undefined : 0}
          className={showAdminStoreLookup && isAdmin ? "flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-4" : "min-h-0 flex-1 overflow-y-auto overscroll-contain outline-none"}
        >
          {showAdminStoreLookup && isAdmin ? (
            <AdminStorePicker retailer={retailer} stores={loadedStores} selectedStoreId={selectedStoreId} loading={storesLoading} showHeader={false} expanded t={t} onSelect={handleAdminStoreSelect} />
          ) : isPickingStore ? (
            <SheetStoreEditor slotIndex={pickSlot} stores={loadedStores} storesLoading={storesLoading} onDone={() => setPickSlot(null)} />
          ) : (
          <>
          {!shouldHideStoresTab && activeTab === "stores" && (
              <div className="px-5 py-3">
                {(hasOnlineOption || selectedStoreId === "online") && (
                  <div className="mb-2 flex gap-1 rounded-xl bg-gray-100 p-1 dark:bg-dark-800" role="group" aria-label={t("products.selectLocationFilter")}>
                    <button type="button" aria-pressed={selectedStoreId !== "online"} onClick={() => { if (selectedStoreId === "online") onStoreChange("all"); }} className={`flex min-h-8 flex-1 items-center justify-center gap-2 rounded-lg px-2 text-[13px] font-medium transition-colors ${selectedStoreId !== "online" ? "bg-primary-600 text-white" : "text-gray-500 hover:bg-gray-200/60 dark:text-dark-300 dark:hover:bg-dark-700"}`}>
                      <Store size={18} strokeWidth={1.75} />{t("products.inStore")}
                    </button>
                    <button type="button" aria-pressed={selectedStoreId === "online"} onClick={() => onStoreChange("online")} className={`flex min-h-8 flex-1 items-center justify-center gap-2 rounded-lg px-2 text-[13px] font-medium transition-colors ${selectedStoreId === "online" ? "bg-primary-600 text-white" : "text-gray-500 hover:bg-gray-200/60 dark:text-dark-300 dark:hover:bg-dark-700"}`}>
                      <Globe size={18} strokeWidth={1.75} />{t("products.onlineDeals")}
                    </button>
                  </div>
                )}
                {selectedStoreId === "online" ? (
                  <p className="py-5 text-sm text-gray-500 dark:text-dark-300">{t("products.browseOnlineDeals")}</p>
                ) : (
                  <>
                    {storeLockedUntil && (
                      <div className="mb-1.5 flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2.5 dark:bg-amber-400/10">
                        <Clock3 size={16} strokeWidth={2} className="shrink-0 text-amber-700 dark:text-amber-400" aria-hidden="true" />
                        <p className="min-w-0 flex-1 text-[13px] font-semibold leading-[18px] text-amber-800 dark:text-amber-200">{t("storeEdit.locked", { date: storeLockedUntil })}</p>
                      </div>
                    )}
                    <div role="radiogroup" aria-label={t("products.storesTab")} className="divide-y divide-gray-200/80 dark:divide-dark-700">
                      {(() => {
                        const extraStoreCount = storeFilterOptions.filter((option) => option.isExtra).length;
                        const allDescription = extraStoreCount > 0
                          ? t("products.allSavedAndExtraStoresDescription", { count: extraStoreCount })
                          : t("products.allSavedStoresDescription");
                        return storeFilterOptions.map((option, index) => {
                        const isSelected = selectedStoreId === option.id;
                        const startsExtraStores = option.isExtra && !storeFilterOptions[index - 1]?.isExtra;
                        const isAll = option.isAll || option.id === "all";
                        const addressParts = isAll ? [] : (storeAddress(option.id) || option.name).split(",").map((part) => part.trim()).filter(Boolean);
                        const street = addressParts[0] || option.name;
                        const city = addressParts[1];
                        const slotIndex = isAll ? -1 : savedStoreIds.indexOf(formatHomeDepotStoreId(option.id));
                        const detail = option.isExtra && option.distanceMiles != null
                          ? [`#${option.id}`, street !== option.name ? street : "", t("products.extraStoreDistance", { miles: option.distanceMiles })].filter(Boolean).join(" · ")
                          : city ? `#${option.id} · ${street}` : `#${option.id}`;
                        return (
                          <Fragment key={option.id}>
                          {startsExtraStores && (
                            <p className="pb-1.5 pt-4 text-[12px] font-bold uppercase tracking-wide text-gray-500 dark:text-dark-300">{t("products.extraStoresHeading")}</p>
                          )}
                          <div className={`group flex items-center gap-2 ${isAll ? "min-h-14" : "min-h-[58px]"}`}>
                            <label className="relative flex min-w-0 flex-1 cursor-pointer items-center gap-3 self-stretch py-2 transition-colors hover:bg-gray-50 focus-within:bg-gray-50 dark:hover:bg-dark-800/60 dark:focus-within:bg-dark-800/60">
                              <input type="radio" name={`${modalId}-store`} value={option.id} checked={isSelected} onChange={() => onStoreChange(option.id)} aria-label={isAll ? t("products.allStoresLabel") : `#${option.id}, ${addressParts.join(", ") || option.name}`} className="sr-only" />
                              {isSelected ? <CircleCheck size={23} strokeWidth={1.75} className="shrink-0 fill-primary-600 text-primary-600 [&>path]:stroke-white" aria-hidden="true" /> : <Circle size={23} strokeWidth={1.25} className="shrink-0 text-gray-400 dark:text-dark-400" aria-hidden="true" />}
                              <span className="min-w-0 flex-1">
                                <span className="flex min-w-0 items-center gap-1.5">
                                  <span className="truncate text-[15px] font-bold leading-5 text-gray-900 dark:text-white">{isAll ? t("products.allStoresLabel") : city || street}</span>
                                  {option.isExtra && (
                                    <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold leading-4 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300">{t("products.extraStoreBadge")}</span>
                                  )}
                                </span>
                                <span className="mt-0.5 block truncate text-[13px] leading-[17px] text-gray-500 dark:text-dark-300">{isAll ? allDescription : detail}</span>
                              </span>
                            </label>
                            {option.isExtra && onHideExtraStore && (
                              <button
                                type="button"
                                onClick={() => onHideExtraStore(option.id)}
                                aria-label={t("products.hideExtraStoreA11y", { store: `${city || street} #${option.id}` })}
                                title={t("products.hideExtraStore")}
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-400 transition-[opacity,color,background-color] hover:bg-gray-100 hover:text-gray-700 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gray-500 dark:text-dark-300 dark:hover:bg-dark-700 dark:hover:text-dark-50 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100"
                              >
                                <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" />
                              </button>
                            )}
                            {slotIndex >= 0 && (
                              <button
                                type="button"
                                onClick={() => openStoreSlot(slotIndex)}
                                aria-label={t("storeEdit.editA11y", { id: option.id })}
                                aria-disabled={storeLockedUntil ? true : undefined}
                                className={`shrink-0 rounded-full bg-purple-50 px-3 py-1.5 text-[13px] font-bold text-primary-600 transition-colors hover:bg-purple-100 dark:bg-purple-400/15 dark:text-purple-300 dark:hover:bg-purple-400/25 ${storeLockedUntil ? "opacity-45" : ""}`}
                              >
                                {t("storeEdit.edit")}
                              </button>
                            )}
                          </div>
                          </Fragment>
                        );
                      });
                      })()}
                      {hiddenExtraStoreOptions.length > 0 && (
                        <>
                          {!storeFilterOptions.some((option) => option.isExtra) && (
                            <p className="pb-1.5 pt-4 text-[12px] font-bold uppercase tracking-wide text-gray-500 dark:text-dark-300">{t("products.extraStoresHeading")}</p>
                          )}
                          <div>
                            <button
                              type="button"
                              aria-expanded={showHiddenExtraStores}
                              aria-controls={`${modalId}-hidden-extra-stores`}
                              onClick={() => setShowHiddenExtraStores((open) => !open)}
                              className="flex min-h-[52px] w-full items-center gap-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-dark-800/60"
                            >
                              <EyeOff size={21} strokeWidth={1.75} className="mx-px shrink-0 text-gray-400 dark:text-dark-400" aria-hidden="true" />
                              <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-gray-600 dark:text-dark-200">{t("products.hiddenExtraStoresToggle", { count: hiddenExtraStoreOptions.length })}</span>
                              <ChevronDown size={18} strokeWidth={2} className={`mr-2 shrink-0 text-gray-400 transition-transform dark:text-dark-400 ${showHiddenExtraStores ? "rotate-180" : ""}`} aria-hidden="true" />
                            </button>
                            {showHiddenExtraStores && (
                              <ul id={`${modalId}-hidden-extra-stores`} className="pb-1">
                                {hiddenExtraStoreOptions.map((option) => {
                                  const addressParts = (storeAddress(option.id) || option.name).split(",").map((part) => part.trim()).filter(Boolean);
                                  const street = addressParts[0] || option.name;
                                  const title = addressParts[1] || street;
                                  const detail = [`#${option.id}`, street !== title ? street : "", option.distanceMiles != null ? t("products.extraStoreDistance", { miles: option.distanceMiles }) : ""].filter(Boolean).join(" · ");
                                  return (
                                    <li key={option.id} className="flex min-h-[52px] items-center gap-3 py-1.5 pl-[35px]">
                                      <span className="min-w-0 flex-1">
                                        <span className="block truncate text-[14px] font-semibold leading-5 text-gray-500 dark:text-dark-300">{title}</span>
                                        <span className="mt-0.5 block truncate text-[12.5px] leading-4 text-gray-400 dark:text-dark-400">{detail}</span>
                                      </span>
                                      {onShowExtraStore && (
                                        <button
                                          type="button"
                                          onClick={() => onShowExtraStore(option.id)}
                                          aria-label={t("products.showHiddenExtraStoreA11y", { store: `${title} #${option.id}` })}
                                          className="shrink-0 rounded-full bg-gray-100 px-3 py-1.5 text-[13px] font-bold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-dark-700 dark:text-dark-100 dark:hover:bg-dark-600"
                                        >
                                          {t("products.showHiddenExtraStore")}
                                        </button>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        </>
                      )}
                      {Array.from({ length: openSlotCount }, (_, offset) => (
                        <button
                          key={`add-${offset}`}
                          type="button"
                          onClick={() => openStoreSlot(savedStoreIds.length + offset)}
                          className="flex min-h-[52px] w-full items-center gap-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-dark-800/60"
                        >
                          <CirclePlus size={23} strokeWidth={1.75} className="shrink-0 fill-primary-600 text-primary-600 [&>path]:stroke-white dark:fill-purple-400 dark:text-purple-400 dark:[&>path]:stroke-dark-900" aria-hidden="true" />
                          <span className="text-[15px] font-bold text-primary-600 dark:text-purple-300">{t("storeEdit.add")}</span>
                        </button>
                      ))}
                    </div>
                    {storeFilterOptions.length === 0 && openSlotCount === 0 && <p className="py-5 text-sm text-gray-500 dark:text-dark-300">{t("products.noStoresAssigned")}</p>}
                    {storeSlots.showProUpgrade && storeSlots.isAtPlanLimit && (
                      <button
                        type="button"
                        onClick={() => { onClose(); navigate("/settings?tab=billing&upgradeSource=filters_stores"); }}
                        className="block w-full py-3.5 text-center text-[13.5px] text-gray-500 dark:text-dark-300"
                      >
                        {t("storeEdit.more")} <span className="font-bold text-primary-600 dark:text-purple-300">{t("storeEdit.moreCta")}</span>
                      </button>
                    )}
                    {adminStoreLookupButton && <div className="flex justify-center border-t border-gray-200/80 pt-1 dark:border-dark-700">{adminStoreLookupButton}</div>}
                  </>
                )}
              </div>
          )}
          {activeTab === "categories" && (
            <div className="px-5 py-4">
              {/* Search */}
              <div className="relative mb-3">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-500 pointer-events-none" />
                <input
                  type="text"
                  placeholder={t("products.searchCategories")}
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className={`${FILTER_INPUT_CLASS} pl-9 pr-9`}
                />
                {categorySearch && (
                  <button
                    onClick={() => setCategorySearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-300 hover:bg-gray-200/70 dark:hover:bg-dark-700 transition-colors"
                    aria-label={t("card.clearCategorySearch")}
                  >
                    <X size={13} />
                  </button>
                )}
              </div>

              {/* Selection summary and select all / clear */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 dark:text-dark-400">
                  {t("products.categoriesSelectedCount", { count: selectedCategoryCount, total: selectableCategoryIDs.length })}
                </span>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => onCategoriesChange([])}
                    disabled={allCategoriesSelected}
                    className="text-xs font-medium text-purple-600 dark:text-purple-400 hover:underline disabled:opacity-40 disabled:no-underline"
                  >
                    {t("products.selectAllCategories")}
                  </button>
                  <button
                    type="button"
                    onClick={() => onCategoriesChange([NO_CATEGORIES])}
                    disabled={selectedCategoryCount === 0}
                    className="text-xs font-medium text-purple-600 dark:text-purple-400 hover:underline disabled:opacity-40 disabled:no-underline"
                  >
                    {t("products.clearCategories")}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-4">
                {categoriesLoading ? (
                  <div role="status" aria-label={t("common.loadingCategories")} className="col-span-2 flex justify-center py-8">
                    <div className="w-6 h-6 border-3 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : filteredSelectableCategories.length === 0 ? (
                  <div className="col-span-2 text-center py-6 text-sm text-gray-500 dark:text-dark-400">
                    {categorySearch ? t("products.noCategoriesMatch", { search: categorySearch }) : t("common.noCategoriesFound")}
                  </div>
                ) : (
                  filteredSelectableCategories.map((category) => {
                    const isChecked = isProductCategorySelected(selectedCategories, category.rootKey);
                    return (
                      <label
                        key={category.rootKey}
                        className="flex items-center gap-2 py-2 border-b border-gray-100 dark:border-dark-700/50 cursor-pointer min-w-0"
                      >
                        <input
                          type="checkbox"
                          name={`${modalId}-category`}
                          checked={isChecked}
                          onChange={() => {
                            onCategoriesChange(toggleProductCategorySelection(selectedCategories, category.rootKey, selectableCategoryIDs));
                          }}
                          className="w-3.5 h-3.5 accent-purple-600 rounded flex-shrink-0 cursor-pointer"
                        />
                        <span className={`text-[13px] truncate ${isChecked ? "text-gray-900 dark:text-white font-medium" : "text-gray-500 dark:text-dark-400"}`}>
                          {categoryLabel(category.rootKey)}
                        </span>
                        {category.count !== undefined && (
                          <span className="text-[10px] text-gray-400 dark:text-dark-500 flex-shrink-0 ml-auto">
                            {category.count}
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>

            </div>
          )}

          {activeTab === "price" && (
            <div className="px-5 py-4">

              {/* Store Price Range */}
              <div className="mb-6">
                <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  {t("products.storePriceRange")}
                </h5>

                <div className="grid grid-cols-2 gap-3">
                  {/* Min Store Price Input */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-dark-300 mb-1.5 block">
                      {t("products.minimumPrice")}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 dark:text-dark-400 text-sm font-medium">
                          $
                        </span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        max="10000"
                        step="0.01"
                        placeholder="0.00"
                        aria-label={t("products.minimumPrice")}
                        value={minStorePrice ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          onMinStorePriceChange?.(
                            value ? Number(value) : undefined
                          );
                        }}
                        className={`${FILTER_INPUT_CLASS} pl-7 pr-3`}
                      />
                    </div>
                  </div>

                  {/* Max Store Price Input */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-dark-300 mb-1.5 block">
                      {t("products.maximumPrice")}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 dark:text-dark-400 text-sm font-medium">
                          $
                        </span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        max="10000"
                        step="0.01"
                        placeholder={t("products.noLimit")}
                        aria-label={t("products.maximumPrice")}
                        value={maxStorePrice ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          onMaxStorePriceChange?.(
                            value ? Number(value) : undefined
                          );
                        }}
                        className={`${FILTER_INPUT_CLASS} pl-7 pr-3`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Minimum Discount */}
              <div className="mb-6">
                <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  {t("products.msrpRange")}
                </h5>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-dark-300 mb-1.5 block">
                      {t("products.minimumMsrp")}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 dark:text-dark-400 text-sm font-medium">
                          $
                        </span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        max="10000"
                        step="0.01"
                        placeholder="0.00"
                        aria-label={t("products.minimumMsrp")}
                        value={minMsrp ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          onMinMsrpChange?.(value ? Number(value) : undefined);
                        }}
                        className={`${FILTER_INPUT_CLASS} pl-7 pr-3`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-dark-300 mb-1.5 block">
                      {t("products.maximumMsrp")}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 dark:text-dark-400 text-sm font-medium">
                          $
                        </span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        max="10000"
                        step="0.01"
                        placeholder={t("products.noLimit")}
                        aria-label={t("products.maximumMsrp")}
                        value={maxMsrp ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          onMaxMsrpChange?.(value ? Number(value) : undefined);
                        }}
                        className={`${FILTER_INPUT_CLASS} pl-7 pr-3`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Minimum Discount */}
              <div className="mb-4">
                <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  {t("products.minimumDiscount")}
                </h5>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    placeholder="0"
                    aria-label={t("products.minimumDiscount")}
                    value={minDiscount ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      onMinDiscountChange?.(value ? Number(value) : undefined);
                    }}
                    className={`${FILTER_INPUT_CLASS} pl-3 pr-10`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-dark-400 text-sm font-medium">%</span>
                  </div>
                </div>
                <div className="flex gap-1.5 mt-2">
                  {DISCOUNT_PRESETS.map((preset) => {
                    const isActive = minDiscount === preset;
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => onMinDiscountChange?.(isActive ? undefined : preset)}
                        className={`flex-1 h-8 rounded-lg text-xs font-semibold tabular-nums border transition-colors ${
                          isActive
                            ? "bg-purple-100 dark:bg-purple-900/40 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300"
                            : "bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-600 text-gray-600 dark:text-dark-300 hover:border-gray-300 dark:hover:border-dark-500 hover:bg-gray-50 dark:hover:bg-dark-700"
                        }`}
                      >
                        {preset}%+
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 dark:text-dark-400 mt-2">
                  {t("products.discountHint")}
                </p>
              </div>

              {/* Clear Price Filters Button */}
              {([minStorePrice, maxStorePrice, minMsrp, maxMsrp, minDiscount].some((value) => value !== undefined)) && (
                <button
                  onClick={() => {
                    onMinStorePriceChange?.(undefined);
                    onMaxStorePriceChange?.(undefined);
                    onMinMsrpChange?.(undefined);
                    onMaxMsrpChange?.(undefined);
                    onMinDiscountChange?.(undefined);
                  }}
                  className="w-full py-2 text-sm font-medium text-gray-500 dark:text-dark-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  {t("products.clearPriceFilters")}
                </button>
              )}
            </div>
          )}

          {activeTab === "other" && (
            <div className="px-5 py-1">

              {onProductStatusFilterChange && hasStatusCounts && (
                <section className="border-b border-gray-100 py-3 dark:border-dark-700" aria-labelledby={`${modalId}-status-title`}>
                  <div id={`${modalId}-status-title`} className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-dark-400">
                    {t("card.dealStatus")}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {statusOptions.map((option) => {
                      const isSelected = option.value === productStatusFilter;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => onProductStatusFilterChange(option.value)}
                          className={`flex min-h-11 items-center gap-2 rounded-xl border px-3 text-left text-xs font-semibold transition-colors ${isSelected ? "border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-700 dark:bg-violet-500/15 dark:text-violet-300" : "border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-dark-700 dark:text-dark-200 dark:hover:bg-dark-800"}`}
                        >
                          {isSelected ? <CircleCheck className="h-4 w-4 shrink-0" /> : <Circle className="h-4 w-4 shrink-0 text-gray-300 dark:text-dark-500" />}
                          <span className="min-w-0 flex-1 truncate">{option.label}</span>
                          {option.count !== undefined ? <span className="text-[10px] tabular-nums opacity-65">{option.count.toLocaleString()}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {onManageHiddenProducts && (
                <button
                  type="button"
                  onClick={() => { onClose(); onManageHiddenProducts(); }}
                  className="flex min-h-12 w-full items-center gap-3 border-b border-gray-100 py-3 text-left dark:border-dark-700"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                    <EyeOff className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-gray-900 dark:text-white">Hidden products</span>
                    <span className="block text-xs text-gray-500 dark:text-dark-400">{hiddenProductsCount.toLocaleString()} hidden · review or restore</span>
                  </span>
                </button>
              )}

              <div className="divide-y divide-gray-100 dark:divide-dark-700">
                <div className="flex items-center justify-between py-3 cursor-pointer select-none" onClick={() => onShowNewestOnlyChange(!showNewestOnly)}>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {t("products.newestItemsOnly")}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-dark-400 mt-0.5">
                      {t("products.newestItemsDescription")}
                    </div>
                  </div>
                  <ToggleSwitch label={t("products.newestItemsOnly")} checked={showNewestOnly} onChange={onShowNewestOnlyChange} />
                </div>

                {onShowVariantsOnlyChange && (
                  <div className="flex items-center justify-between py-3 cursor-pointer select-none" onClick={() => onShowVariantsOnlyChange(!showVariantsOnly)}>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {t("products.variantsOnly")}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-dark-400 mt-0.5">
                        {t("products.variantsOnlyDesc")}
                      </div>
                    </div>
                    <ToggleSwitch label={t("products.variantsOnly")} checked={showVariantsOnly} onChange={onShowVariantsOnlyChange} />
                  </div>
                )}


                <div className="flex items-center justify-between py-3 cursor-pointer select-none" onClick={() => onShowInStockOnlyChange?.(!showInStockOnly)}>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {t("products.inStockOnly")}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-dark-400 mt-0.5">
                      {t("products.inStockOnlyDesc")}
                    </div>
                  </div>
                  <ToggleSwitch label={t("products.inStockOnly")} checked={showInStockOnly} onChange={(v) => onShowInStockOnlyChange?.(v)} />
                </div>

                {supportsCrazyDeals(retailer) && (
                  <div className="flex items-center justify-between py-3 cursor-pointer select-none" onClick={() => setShowHotDeals(!showHotDeals)}>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {t("products.hotDeals")}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-dark-400 mt-0.5">
                        {t("products.hotDealsDesc")}
                      </div>
                    </div>
                    <ToggleSwitch label={t("products.hotDeals")} checked={showHotDeals} onChange={setShowHotDeals} />
                  </div>
                )}

                {(retailer === "walmart" || retailer === "dollar_general") && !isOnlineRetailer && selectedStoreId !== "online" && onHideCashbackChange && (
                  <div className="flex items-center justify-between py-3 cursor-pointer select-none" onClick={() => onHideCashbackChange(!hideCashback)}>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {t("products.hideCashback")}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-dark-400 mt-0.5">
                        {t("products.hideCashbackDesc")}
                      </div>
                    </div>
                    <ToggleSwitch label={t("products.hideCashback")} checked={hideCashback} onChange={onHideCashbackChange} />
                  </div>
                )}

                {(retailer === "home_depot" || retailer === "dollar_general") && (
                  <div className="flex items-center justify-between py-3 cursor-pointer select-none" onClick={() => setShowPennyItems()}>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {t(retailer === "dollar_general" ? "products.potentialPennyItems" : "products.pennyItems")}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-dark-400 mt-0.5">
                        {t(retailer === "dollar_general" ? "products.potentialPennyItemsDesc" : "products.pennyItemsDesc")}
                      </div>
                    </div>
                    <ToggleSwitch label={t("products.pennyItems")} checked={showPennyItems} onChange={() => setShowPennyItems()} />
                  </div>
                )}
              </div>
            </div>
          )}
          </>
          )}
        </div>

        {!showSubview && (
          <div className="flex shrink-0 items-center gap-4 border-t border-gray-200/80 px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-dark-700">
            {onResetFilters && (
              <button type="button" onClick={() => { onResetFilters(); setCategorySearch(""); }} className="flex min-h-11 flex-1 items-center gap-2 whitespace-nowrap rounded-lg px-1 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:text-dark-300 dark:hover:bg-dark-800">
                <RotateCcw size={18} strokeWidth={1.75} className="shrink-0" />{t("products.resetAll")}
              </button>
            )}
            <button type="button" onClick={onClose} className="min-h-11 flex-1 rounded-xl bg-primary-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-primary-700">
              {t("common.done")}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ProductFiltersModal;
